/**
 * Firm Mode context: detects Arsheef token from URL or sessionStorage,
 * validates with backend, and exposes mode (public | firm) and firm user info.
 * Does not modify public auth (register/login); public mode works as before.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS, buildApiUrl } from '../config/apiConfig';
import {
  mergeActiveFirmCaseFromPartial,
  trimStr,
  FIRM_CONTEXT_SHORT_DESC_MAX,
  FIRM_CONTEXT_SUMMARY_MAX,
  FIRM_CONTEXT_DESCRIPTION_MAX,
} from '../utils/firmCaseContextPayload';

const FirmModeContext = createContext();

export const useFirmMode = () => {
  const context = useContext(FirmModeContext);
  if (!context) throw new Error('useFirmMode must be used within FirmModeProvider');
  return context;
};

const STORAGE_KEY_MODE = 'ydim_firm_mode';
const STORAGE_KEY_PAYLOAD = 'ydim_firm_payload';
const STORAGE_KEY_TOKEN = 'ydim_arsheef_token';
const STORAGE_KEY_ACTIVE_CASE = 'ydim_active_firm_case';
/** Token captured in index.js before React; used if URL was cleared before effect runs */
const STORAGE_KEY_TOKEN_PENDING = 'ydim_arsheef_token_pending';

/** Log in development or when running on localhost (e.g. production build on :8080) */
const shouldLogFirmMode = () => {
  if (typeof window === 'undefined') return false;
  if (process.env.NODE_ENV === 'development') return true;
  return /localhost|127\.0\.0\.1/.test(window.location.hostname || '');
};

/** Firm case context for sends: detail fields + optional trimmed document metadata. */
export function FirmModeProvider({ children }) {
  const [mode, setMode] = useState('public');
  const [firmPayload, setFirmPayload] = useState(null);
  const [firmCheckDone, setFirmCheckDone] = useState(false);
  const [activeFirmCase, setActiveFirmCaseState] = useState(null);

  const clearFirmMode = useCallback(() => {
    setMode('public');
    setFirmPayload(null);
    setActiveFirmCaseState(null);
    try {
      sessionStorage.removeItem(STORAGE_KEY_MODE);
      sessionStorage.removeItem(STORAGE_KEY_PAYLOAD);
      sessionStorage.removeItem(STORAGE_KEY_TOKEN);
      sessionStorage.removeItem(STORAGE_KEY_ACTIVE_CASE);
    } catch (e) {
      // ignore
    }
  }, []);

  /**
   * @param {object|null} caseData - Case detail from /api/firm/cases/:id
   * @param {object[]|null|undefined} documentsList - Raw document rows from /api/firm/documents (optional)
   */
  const setActiveFirmCase = useCallback((caseData, documentsList) => {
    if (!caseData) {
      setActiveFirmCaseState(null);
      try {
        sessionStorage.removeItem(STORAGE_KEY_ACTIVE_CASE);
      } catch (e) {
        // ignore
      }
      return;
    }
    const docRows = Array.isArray(documentsList) ? documentsList : [];
    const merged = mergeActiveFirmCaseFromPartial({
      ...caseData,
      documents: docRows,
    });
    if (!merged) {
      setActiveFirmCaseState(null);
      try {
        sessionStorage.removeItem(STORAGE_KEY_ACTIVE_CASE);
      } catch (e) {
        // ignore
      }
      return;
    }
    const normalized = {
      ...merged,
      shortDescription: trimStr(merged.shortDescription, FIRM_CONTEXT_SHORT_DESC_MAX),
      summary: trimStr(merged.summary, FIRM_CONTEXT_SUMMARY_MAX),
      description: trimStr(merged.description, FIRM_CONTEXT_DESCRIPTION_MAX),
    };
    if (shouldLogFirmMode()) {
      console.log('[FirmMode] enriched activeFirmCase (stored)', normalized);
    }
    setActiveFirmCaseState(normalized);
    try {
      sessionStorage.setItem(STORAGE_KEY_ACTIVE_CASE, JSON.stringify(normalized));
    } catch (e) {
      // ignore
    }
  }, []);

  const clearActiveFirmCase = useCallback(() => {
    setActiveFirmCaseState(null);
    try {
      sessionStorage.removeItem(STORAGE_KEY_ACTIVE_CASE);
    } catch (e) {
      // ignore
    }
  }, []);

  /** Token for Authorization header when calling /api/firm/*. Null in public mode or after expiry. */
  const getFirmToken = useCallback(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY_TOKEN);
    } catch (e) {
      return null;
    }
  }, []);

  const establishFirmMode = useCallback(async (token) => {
    if (!token || !token.trim()) return;
    const trimmedToken = token.trim();
    const url =
      typeof window !== 'undefined'
        ? '/api/auth/firm'
        : buildApiUrl(API_ENDPOINTS.AUTH_FIRM);
    if (shouldLogFirmMode()) {
      console.log('[FirmMode] Token detected in URL, sending POST /api/auth/firm');
    }
    try {
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${trimmedToken}`,
        },
      });
      const data = await res.json();
      if (shouldLogFirmMode()) {
        console.log('[FirmMode] Response received (full):', JSON.stringify(data, null, 2));
      }
      const shouldPersist =
        data &&
        data.mode === 'firm' &&
        (data.arsheefUserId !== undefined && data.arsheefUserId !== null);
      if (shouldPersist) {
        let officeContext = null;
        try {
          const meRes = await fetch(
            typeof window !== 'undefined' ? '/api/firm/me' : buildApiUrl(API_ENDPOINTS.FIRM_ME),
            {
              method: 'GET',
              headers: { Authorization: `Bearer ${trimmedToken}` },
              credentials: 'include',
            }
          );
          if (meRes.ok) {
            const meData = await meRes.json();
            officeContext = {
              officeId: meData?.officeId || null,
              officeBilling: meData?.officeBilling || null,
            };
            if (shouldLogFirmMode()) {
              console.log('[FirmMode] /api/firm/me office context loaded');
            }
          }
        } catch (err) {
          if (shouldLogFirmMode()) {
            console.warn('[FirmMode] /api/firm/me office context load failed:', err?.message || err);
          }
        }

        const payload = {
          arsheefUserId: data.arsheefUserId,
          email: data.email || '',
          roleCodes: Array.isArray(data.roleCodes) ? data.roleCodes : [],
          permissions: Array.isArray(data.permissions) ? data.permissions : [],
          officeId: officeContext?.officeId || null,
          officeBilling: officeContext?.officeBilling || null,
        };
        setMode('firm');
        setFirmPayload(payload);
        if (shouldLogFirmMode()) {
          console.log('[FirmMode] About to write sessionStorage:', [
            STORAGE_KEY_MODE,
            STORAGE_KEY_PAYLOAD,
            STORAGE_KEY_TOKEN,
          ]);
        }
        try {
          sessionStorage.setItem(STORAGE_KEY_MODE, 'firm');
          sessionStorage.setItem(STORAGE_KEY_PAYLOAD, JSON.stringify(payload));
          sessionStorage.setItem(STORAGE_KEY_TOKEN, trimmedToken);
        } catch (e) {
          if (shouldLogFirmMode()) {
            console.warn('[FirmMode] sessionStorage write failed:', e?.message || e);
          }
        }
        if (shouldLogFirmMode()) {
          console.log('[FirmMode] Firm mode activated; token saved to sessionStorage; clearing token from URL');
        }
        if (typeof window !== 'undefined') {
          const params = new URLSearchParams(window.location.search);
          const hashParams = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
          if (params.get('arsheef_token') || hashParams.get('arsheef_token')) {
            params.delete('arsheef_token');
            hashParams.delete('arsheef_token');
            const cleanSearch = params.toString();
            const cleanHash = hashParams.toString();
            const newUrl =
              window.location.pathname +
              (cleanSearch ? `?${cleanSearch}` : '') +
              (cleanHash ? `#${cleanHash}` : '');
            window.history.replaceState({}, '', newUrl);
          }
        }
      } else {
        if (shouldLogFirmMode()) {
          const reason =
            !data
              ? 'no data'
              : data.mode !== 'firm'
                ? `mode is "${data.mode}" (expected "firm")`
                : data.arsheefUserId === undefined
                  ? 'arsheefUserId is undefined'
                  : data.arsheefUserId === null
                    ? 'arsheefUserId is null'
                    : 'unknown';
          console.log('[FirmMode] NOT persisting firm mode:', reason);
        }
        setMode('public');
        setFirmPayload(null);
      }
    } catch (err) {
      if (shouldLogFirmMode()) {
        console.warn('[FirmMode] POST /api/auth/firm failed:', err?.message || err);
      }
      setMode('public');
      setFirmPayload(null);
    } finally {
      setFirmCheckDone(true);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      console.log('[FirmMode] Provider effect ran');
    }
    const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const hashParams =
      typeof window !== 'undefined'
        ? new URLSearchParams((window.location.hash || '').replace(/^#/, ''))
        : null;
    const urlToken = (params ? params.get('arsheef_token') : null) || (hashParams ? hashParams.get('arsheef_token') : null);
    const pendingToken =
      typeof window !== 'undefined' ? sessionStorage.getItem(STORAGE_KEY_TOKEN_PENDING) : null;
    const tokenToUse = (urlToken && urlToken.trim()) || (pendingToken && pendingToken.trim()) || null;

    if (tokenToUse) {
      if (pendingToken && typeof window !== 'undefined') {
        try {
          sessionStorage.removeItem(STORAGE_KEY_TOKEN_PENDING);
        } catch (e) {
          // ignore
        }
      }
      if (shouldLogFirmMode()) {
        console.log('[FirmMode] Token from', urlToken ? 'URL' : 'bootstrap (pending)', '- establishing firm mode');
      }
      establishFirmMode(tokenToUse);
      return;
    }

    try {
      const storedMode = sessionStorage.getItem(STORAGE_KEY_MODE);
      const storedPayload = sessionStorage.getItem(STORAGE_KEY_PAYLOAD);
      if (storedMode === 'firm' && storedPayload) {
        setMode('firm');
        setFirmPayload(JSON.parse(storedPayload));
        const storedCase = sessionStorage.getItem(STORAGE_KEY_ACTIVE_CASE);
        if (storedCase) {
          try {
            const parsed = JSON.parse(storedCase);
            const merged = mergeActiveFirmCaseFromPartial(parsed);
            if (merged) {
              setActiveFirmCaseState({
                ...merged,
                shortDescription: trimStr(merged.shortDescription, FIRM_CONTEXT_SHORT_DESC_MAX),
                summary: trimStr(merged.summary, FIRM_CONTEXT_SUMMARY_MAX),
                description: trimStr(merged.description, FIRM_CONTEXT_DESCRIPTION_MAX),
              });
            }
          } catch (e) {
            // ignore
          }
        }
        if (shouldLogFirmMode()) {
          console.log('[FirmMode] Restored firm mode from sessionStorage');
        }
      }
    } catch (e) {
      // ignore
    }
    setFirmCheckDone(true);
  }, [establishFirmMode]);

  /**
   * If firm mode was established via URL token, the effect above returned early and did not
   * restore ydim_active_firm_case. Re-load stored case once firm mode is ready.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!firmCheckDone || mode !== 'firm') return;
    setActiveFirmCaseState((prev) => {
      if (prev != null) return prev;
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY_ACTIVE_CASE);
        if (!raw) return prev;
        const parsed = JSON.parse(raw);
        const merged = mergeActiveFirmCaseFromPartial(parsed);
        if (!merged) return prev;
        if (shouldLogFirmMode()) {
          console.log('[FirmMode] restored activeFirmCase after firm init (e.g. URL token path)', merged);
        }
        return {
          ...merged,
          shortDescription: trimStr(merged.shortDescription, FIRM_CONTEXT_SHORT_DESC_MAX),
          summary: trimStr(merged.summary, FIRM_CONTEXT_SUMMARY_MAX),
          description: trimStr(merged.description, FIRM_CONTEXT_DESCRIPTION_MAX),
        };
      } catch {
        return prev;
      }
    });
  }, [firmCheckDone, mode]);

  const value = {
    mode,
    firmPayload,
    isFirmMode: mode === 'firm',
    firmCheckDone,
    clearFirmMode,
    establishFirmMode,
    getFirmToken,
    activeFirmCase: mode === 'firm' ? activeFirmCase : null,
    setActiveFirmCase,
    clearActiveFirmCase,
  };

  return (
    <FirmModeContext.Provider value={value}>
      {children}
    </FirmModeContext.Provider>
  );
}
